import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { GeneralHelper } from '@shared/helpers';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private http = inject(HttpClient);

    private readonly API_URL = `${environment.apiUrl}/`;

    private cancelSubjects = new Map<string, Subject<void>>();

    get(path: string, params = {}): Observable<any> {
        return this.http.get(`${this.API_URL}${path}`, { params: params }).pipe(
            catchError(error => {
                if (error.error === 'login_required') {
                    window.location.reload();
                }
                return throwError(error);
            })
        );
    }

    put(path: string, body = {}): Observable<any> {
        const trimmedBody = GeneralHelper.trimStrings(body);

        return this.http.put(`${this.API_URL}${path}`, trimmedBody).pipe(
            catchError(error => {
                if (error.error === 'login_required') {
                    window.location.reload();
                }
                return throwError(error);
            })
        );
    }

    post(path: string, body = {}, params = {}, responseType: 'json' | 'text' = 'json'): Observable<any> {
        const trimmedBody = GeneralHelper.trimStrings(body);

        this.checkIfShouldCancel(path, trimmedBody);

        const cancelSubject = new Subject<void>();
        const existingCancelSubject = this.cancelSubjects.get(path);

        if (existingCancelSubject) {
            existingCancelSubject.next();
            existingCancelSubject.complete();
        }

        this.cancelSubjects.set(path, cancelSubject);

        const observable = this.http
            .post(`${this.API_URL}${path}`, trimmedBody, { params: params, responseType: responseType as any })
            .pipe(
                takeUntil(cancelSubject),
                catchError(error => {
                    if (error.error === 'login_required') {
                        window.location.reload();
                    }
                    return throwError(() => error);
                }),
                finalize(() => {
                    const currentSubject = this.cancelSubjects.get(path);
                    if (currentSubject === cancelSubject) {
                        this.cancelSubjects.delete(path);
                    }
                })
            );

        return observable;
    }

    delete(path: string, body = {}): Observable<any> {
        return this.http.delete(`${this.API_URL}${path}`, { body }).pipe(
            catchError(error => {
                if (error.error === 'login_required') {
                    window.location.reload();
                }
                return throwError(error);
            })
        );
    }

    patch(path: string, body = {}): Observable<any> {
        const trimmedBody = GeneralHelper.trimStrings(body);

        return this.http.patch(`${this.API_URL}${path}`, trimmedBody).pipe(
            catchError(error => {
                if (error.error === 'login_required') {
                    window.location.reload();
                }
                return throwError(error);
            })
        );
    }

    download(path: string): Observable<any> {
        return this.http.post(`${this.API_URL}${path}`, {}, { responseType: 'blob' }).pipe(
            catchError(error => {
                if (error.error === 'login_required') {
                    window.location.reload();
                }
                return throwError(error);
            })
        );
    }

    private checkIfShouldCancel(path: string, body: any): void {
        if (!this.hasTakeProperty(body)) {
            return;
        }

        const existingCancelSubject = this.cancelSubjects.get(path);
        if (!existingCancelSubject) {
            return;
        }

        if (existingCancelSubject.closed) {
            return;
        }

        existingCancelSubject.next();
        existingCancelSubject.complete();
        this.cancelSubjects.delete(path);
    }

    private hasTakeProperty(obj: any, visited: Set<any> = new Set()): boolean {
        if (obj === null || obj === undefined) {
            return false;
        }

        if (visited.has(obj)) {
            return false;
        }

        if (typeof obj !== 'object') {
            return false;
        }

        if (Array.isArray(obj)) {
            visited.add(obj);
            return obj.some(item => this.hasTakeProperty(item, visited));
        }

        if ('take' in obj) {
            return true;
        }

        visited.add(obj);

        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (this.hasTakeProperty(obj[key], visited)) {
                    return true;
                }
            }
        }

        return false;
    }
}
